import type { RecordsSeriesJson } from "@/api/recordsSeries";
import type { ChartData, ChartLine } from "@/components/chartLines";
import type { ShareData } from "@/components/chartSlices";
import { PIPELINE_COLORS, SERIES_COLORS } from "@/components/seriesPalette";

// A windowed pipeline's store over time, as the engine serves it at
// /reports/pipelines/{name}/series/windows: the records series' envelope,
// one view of the store per document, and beside the values how much of the
// store each moment's sample could read and under which stream identity.
//
// Each moment is the newest sample any replica took of the shared store,
// never a sum. A null value is a moment with nothing to answer from; the
// coverage at that moment says why.

export const WINDOW_VIEWS = [
  "group_windows",
  "output_records",
  "sink_deliveries",
  "partition_states",
] as const;
export type WindowView = (typeof WINDOW_VIEWS)[number];

/** The lines each view draws, in the order the engine serves them. */
export const WINDOW_VIEW_LINES: Record<WindowView, readonly string[]> = {
  group_windows: ["settled", "open"],
  output_records: ["settled"],
  sink_deliveries: ["settled"],
  partition_states: [
    "idle",
    "busy",
    "retry",
    "failed",
    "unassigned",
    "unknown",
  ],
};

export type WindowCoverage = "complete" | "partial" | "unavailable" | "stale";

export interface WindowSeriesJson extends RecordsSeriesJson {
  coverage: Array<WindowCoverage | null>;
  identities: Array<{
    scope: string;
    incarnation: string | null;
    generation: number | null;
    marker: string | null;
    from: string;
    to: string;
  }>;
  expected_partitions: Array<number | null>;
}

/** A line's colour: the counters in the records chart's own hues, the
 *  partition states in hues that read as what they are. */
const LINE_COLORS: Record<string, string> = {
  settled: SERIES_COLORS.watched,
  open: SERIES_COLORS.dropped,
  idle: PIPELINE_COLORS[2] ?? SERIES_COLORS.watched, // green
  busy: PIPELINE_COLORS[4] ?? SERIES_COLORS.watched, // blue
  retry: PIPELINE_COLORS[1] ?? SERIES_COLORS.watched, // orange
  failed: PIPELINE_COLORS[0] ?? SERIES_COLORS.watched, // red
  unassigned: PIPELINE_COLORS[3] ?? SERIES_COLORS.watched, // cyan
  unknown: PIPELINE_COLORS[6] ?? SERIES_COLORS.watched, // purple
};

function colorOf(name: string): string {
  return LINE_COLORS[name] ?? SERIES_COLORS.watched;
}

/** The view's lines as the chart draws them, narrowed to `series` when the
 *  fence named some. A line the engine left out is unknown throughout. */
export function windowChartData(
  doc: WindowSeriesJson,
  view: WindowView,
  series?: readonly string[],
): ChartData {
  const wanted = WINDOW_VIEW_LINES[view].filter(
    (name) => series === undefined || series.includes(name),
  );
  const lines: ChartLine[] = wanted.map((name) => {
    const served = doc.series.find((s) => s.name === name)?.values ?? [];
    return {
      label: name,
      color: colorOf(name),
      values: doc.times.map((_, i) => served[i] ?? null),
    };
  });
  return { moments: doc.times, lines };
}

/** What the plot cannot say itself: moments read only in part, or not at
 *  all, and where the stream started a new generation. */
export function windowChartNotes(doc: WindowSeriesJson): string[] {
  const unread = doc.coverage.filter(
    (c) => c !== null && c !== "complete",
  ).length;
  const notes = unread > 0 ? [`${unread} moments without a complete read`] : [];
  for (const segment of doc.identities.slice(1)) {
    notes.push(`new generation from ${segment.from}`);
  }
  return notes;
}

/** The partition states at the last moment that read them: coverage
 *  complete or partial, and every state known. Its own moment, never the
 *  window's end, which may be stale or unread. */
export function partitionStatesShare(
  doc: WindowSeriesJson,
  series?: readonly string[],
): ShareData | undefined {
  const names = WINDOW_VIEW_LINES.partition_states;
  const wanted = (name: string) =>
    series === undefined || series.includes(name);
  for (let i = doc.times.length - 1; i >= 0; i--) {
    const values = statesReadAt(doc, i);
    if (values === undefined) continue;
    const slices = names.flatMap((name, k) => {
      const value = values[k] ?? 0;
      return value > 0 && wanted(name)
        ? [{ label: name, color: colorOf(name), value }]
        : [];
    });
    return { at: doc.times[i] ?? "", slices, unread: [] };
  }
  return undefined;
}

/** The partition states at moment `i`, in view order, when that moment's
 *  sample read them whole enough to count; undefined otherwise. */
function statesReadAt(doc: WindowSeriesJson, i: number): number[] | undefined {
  const coverage = doc.coverage[i];
  if (coverage !== "complete" && coverage !== "partial") return undefined;
  const values = WINDOW_VIEW_LINES.partition_states.map(
    (name) => doc.series.find((s) => s.name === name)?.values[i],
  );
  return values.every((v): v is number => typeof v === "number")
    ? values
    : undefined;
}
